import type { StateStorage } from '@ngneat/elf-persist-state';

/**
 * Minimal IndexedDB adapter for `@ngneat/elf-persist-state`.
 *
 * Why in-tree instead of `localforage`: keeps the dependency surface small.
 * Modern browsers all ship IDB; we don't need a cross-backend fallback.
 *
 * One IDB database per call; a single object store (`kv`) holds every key
 * passed to `persistState`. Values are stored as structured-clone objects,
 * so Dates, Maps, Sets and Blobs round-trip natively — no JSON coercion.
 */
export function createIdbStorage(dbName: string, storeName = 'kv'): StateStorage {
  let dbPromise: Promise<IDBDatabase> | undefined;

  const open = (): Promise<IDBDatabase> => {
    if (dbPromise) return dbPromise;
    dbPromise = new Promise((resolve, reject) => {
      const req = indexedDB.open(dbName, 1);
      req.onupgradeneeded = () => {
        if (!req.result.objectStoreNames.contains(storeName)) {
          req.result.createObjectStore(storeName);
        }
      };
      req.onsuccess = () => resolve(req.result);
      req.onerror = () => reject(req.error);
      // Fires when another tab holds an older DB version that blocks our
      // upgrade. We surface it so the caller knows persistence won't work
      // until that tab closes or reloads.
      req.onblocked = () => reject(new Error(`IDB upgrade blocked by another tab (${dbName})`));
    });
    return dbPromise;
  };

  const wrap = <T>(req: IDBRequest<T>): Promise<T> =>
    new Promise((resolve, reject) => {
      req.onsuccess = () => resolve(req.result);
      req.onerror = () => reject(req.error);
    });

  return {
    async getItem<T extends Record<string, unknown>>(key: string): Promise<T | null | undefined> {
      const db = await open();
      const tx = db.transaction(storeName, 'readonly');
      const value = await wrap<T | undefined>(tx.objectStore(storeName).get(key));
      return value ?? null;
    },

    async setItem(key: string, value: Record<string, unknown>): Promise<boolean> {
      const db = await open();
      const tx = db.transaction(storeName, 'readwrite');
      await wrap(tx.objectStore(storeName).put(value, key));
      return true;
    },

    async removeItem(key: string): Promise<boolean> {
      const db = await open();
      const tx = db.transaction(storeName, 'readwrite');
      await wrap(tx.objectStore(storeName).delete(key));
      return true;
    },
  };
}
